import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';

/**
 * Picks the cycle a new bet goes into.
 *
 * Shared by betting on a pitch and betting on work again, which are the same
 * mechanical choice with different meanings. Each caller brings its own words,
 * because "this is attempt three" and "this won its first bet" should not read
 * the same.
 */
const ChooseCycleDialog = ({
  open,
  title,
  description,
  confirmLabel = 'Place the bet',
  cycles = [],
  excludeCycleId = null,
  onClose,
  onSubmit,
}) => {
  const [cycleId, setCycleId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // A closed cycle cannot take new bets, and the cycle a bet is already in is not
  // a place to bet on it again.
  const candidates = cycles.filter(cycle => (
    cycle.id !== excludeCycleId && cycle.status !== 'complete'
  ));

  useEffect(() => {
    if (!open) return;
    // A planning cycle is where a new bet belongs; an active one is underway.
    const planning = candidates.find(cycle => cycle.status === 'planning');
    setCycleId(planning?.id || candidates[0]?.id || '');
    setError(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSubmit(cycleId);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not place the bet.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {description && <DialogContentText sx={{ mb: 2 }}>{description}</DialogContentText>}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {candidates.length === 0 ? (
          <Alert severity="info">
            There is no open cycle to bet into. Create one first.
          </Alert>
        ) : (
          <TextField
            select
            fullWidth
            size="small"
            label="Cycle"
            value={cycleId}
            onChange={(e) => setCycleId(e.target.value)}
          >
            {candidates.map(cycle => (
              <MenuItem key={cycle.id} value={cycle.id}>{cycle.name}</MenuItem>
            ))}
          </TextField>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving || !cycleId}>
          {saving ? 'Placing…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChooseCycleDialog;
